import { useState } from 'react'
import CommsFrame, { ChannelRail, StateBlock } from './CommsShell'
import { COMMS_ROOT, type Channel } from './commsData'

/**
 * CommunicationsHome — /communications (channels) and /communications/dms.
 *
 * PHASE_2I: presentational (the route wrapper owns useChannels/useDms). Left
 * rail = channels + DMs; center = create-a-channel + honest guidance. No
 * fabricated channels or DMs — empty/loading/error/auth are honest.
 *
 * Chat #2 conversion: presentation lives in comms-surface.css; no hex/inline
 * color literals remain.
 */
export interface CommunicationsHomeProps {
  mode: 'channels' | 'dms'
  channels: Channel[]
  dms: Channel[]
  loading: boolean
  error: string | null
  authRequired: boolean
  onOpenChannel?: (id: string) => void
  onOpenDm?: (id: string) => void
  onCreateChannel?: (name: string) => void | Promise<unknown>
  onOpenTeam?: () => void
  onOpenConferenceCalls?: () => void
  onOpenTasks?: () => void
  onHome?: () => void
  onBackCommandCenter?: () => void
  onNavTop?: (to: string) => void
}

export default function CommunicationsHome(props: CommunicationsHomeProps) {
  const { mode, channels, dms, loading, error, authRequired, onOpenChannel, onOpenDm, onCreateChannel, onOpenTeam, onOpenConferenceCalls, onOpenTasks, onHome, onBackCommandCenter, onNavTop } = props
  const [name, setName] = useState('')
  const [creating, setCreating] = useState(false)
  const create = async () => {
    if (!name.trim() || !onCreateChannel) return
    setCreating(true)
    try { await onCreateChannel(name.trim()); setName('') } finally { setCreating(false) }
  }

  const center = (
    <div className="sqv-surface-comms" style={{ gap: 14 }}>
      <div className="sqv-comms-card">
        <h3 className="sqv-comms-title">{mode === 'dms' ? 'Direct messages' : 'Channels'}</h3>
        <p className="sqv-comms-mb12 sqv-comms-c-2" style={{ fontSize: 12.5, lineHeight: 1.6 }}>
          {mode === 'dms'
            ? 'Pick a direct message from the rail, or start one from a teammate in the Team roster. No fabricated participants — DMs appear only when you open one.'
            : 'Pick a channel from the rail to read and post messages. Real, membership-scoped channels — no seeded chat history.'}
        </p>
        <StateBlock
          loading={loading}
          error={error}
          authRequired={authRequired}
          empty={(mode === 'dms' ? dms.length : channels.length) === 0}
          emptyLabel={mode === 'dms' ? 'No direct messages yet — start one from the Team roster.' : 'No channels yet — create your first channel below.'}
        />
        <div style={{ marginTop: 12 }}>
          <button type="button" onClick={onOpenTeam} className="sqv-comms-btn" style={{ textAlign: 'left' }}>
            Start a DM — pick a teammate from the roster
          </button>
          <p className="sqv-comms-hint7 sqv-comms-c-4 sqv-comms-mono-ui" style={{ fontSize: 10.5 }}>
            DMs open against real roster members (a real 2-party channel is created server-side, audited). No fabricated participants.
          </p>
        </div>
      </div>

      {mode === 'channels' && (
        <div className="sqv-comms-card">
          <h3 className="sqv-comms-title">Create a channel</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') create() }} placeholder="e.g. daily-ops" aria-label="New channel name" disabled={authRequired} className="sqv-comms-input" style={{ flex: 1 }} />
            <button type="button" onClick={create} disabled={authRequired || creating} className="sqv-comms-btn">{creating ? 'Creating…' : 'Create'}</button>
          </div>
          <p className="sqv-comms-hint sqv-comms-c-4 sqv-comms-mono-ui" style={{ fontSize: 10.5 }}>
            You become the channel owner. Suggested defaults you might create: #daily-ops · #projects · #clients · #build-log · #blockers (TEMPLATE names — created only if you make them).
          </p>
        </div>
      )}
    </div>
  )

  const rightRail = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className="sqv-comms-card">
        <h3 className="sqv-comms-title">Message actions</h3>
        <p className="sqv-comms-mb10 sqv-comms-c-3" style={{ fontSize: 11.5, lineHeight: 1.6 }}>
          Open a channel to act on a message — Create task and Flag a blocker save real, audited <code className="sqv-comms-mono-ui">sq_team_tasks</code> rows tied to that channel. Receipt / link-to-portal / assign-owner / copy stay clearly deferred.
        </p>
        <button type="button" onClick={onOpenConferenceCalls} className="sqv-comms-btn" style={{ width: '100%', textAlign: 'left' }}>Start a conference call →</button>
        <p className="sqv-comms-hint sqv-comms-c-4 sqv-comms-mono-ui" style={{ fontSize: 10.5 }}>Conference Calls is the Daily.co video layer — separate from the Daily Meetings record under Operations.</p>
      </div>
      <div className="sqv-comms-card">
        <h3 className="sqv-comms-title">Backend status</h3>
        <p className="sqv-comms-m0 sqv-comms-c-4 sqv-comms-mono-ui" style={{ fontSize: 11, lineHeight: 1.6 }}>
          The Message Center persists to the shared comms spine — <span className="sqv-comms-c-green">20260709000002_communications_teams_tasks.sql</span> applied 2026-07-10; channels / DMs / tasks are live records. REALTIME_DEFERRED (refresh to see new messages).
        </p>
      </div>
    </div>
  )

  return (
    <CommsFrame
      activeTop="communications"
      surface={mode === 'dms' ? 'direct-messages' : 'message-center'}
      breadcrumb={mode === 'dms' ? ['COMMAND CENTER', 'MESSAGE CENTER', 'DIRECT MESSAGES'] : ['COMMAND CENTER', 'MESSAGE CENTER']}
      title={mode === 'dms' ? 'Direct Messages' : 'Message Center'}
      realtimeBadge
      onHome={onHome}
      onBackCommandCenter={onBackCommandCenter}
      onNavTop={onNavTop}
      leftRail={<ChannelRail channels={channels} dms={dms} loading={loading} error={error} authRequired={authRequired} onOpenChannel={onOpenChannel} onOpenDm={onOpenDm} onCreateChannel={() => { /* inline form below */ }} onOpenTeam={onOpenTeam} onOpenConferenceCalls={onOpenConferenceCalls} onOpenTasks={onOpenTasks} onOpenHome={() => onNavTop?.(COMMS_ROOT)} activeNav={mode === 'dms' ? 'dms' : 'home'} />}
      center={center}
      rightRail={rightRail}
    />
  )
}
